/**
 * Semantic alignment records between a baseline (vulnerable) and patched
 * call tree. EL-041 produces these; EL-042 reads them to locate the first
 * causal divergence.
 */
import { z } from "zod";
export const AlignmentRelationSchema = z.enum([
    "match",
    "insert",
    "delete",
    "replace",
    "ambiguous",
]);
export const AlignmentRecordSchema = z
    .object({
    baselineNodeId: z.string().min(1).optional(),
    patchedNodeId: z.string().min(1).optional(),
    relation: AlignmentRelationSchema,
    score: z.number().min(0).max(1),
    reasons: z.array(z.string().min(1)),
})
    .refine((record) => record.baselineNodeId !== undefined || record.patchedNodeId !== undefined, {
    message: "an alignment record must reference at least one side",
    path: ["baselineNodeId"],
});
/** The `alignment.json` artifact written once both traces are aligned. */
export const AlignmentFileSchema = z.object({
    schemaVersion: z.literal("1.0"),
    caseId: z.string().min(1),
    records: z.array(AlignmentRecordSchema),
});
/**
 * The `divergence.json` artifact (EL-042). No schema for this existed
 * before this task (checked `schemas/` and every package) — this is its
 * first definition, scoped deliberately minimal and evidence-based per
 * `docs/03_SYSTEM_ARCHITECTURE.md` section 8's safety behavior: "the
 * causal seam must reference evidence on both sides or an explicit
 * insertion/deletion", and low-confidence findings must not be forced into
 * a confident answer.
 *
 * `status: "found"` requires at least one of `baselineNodeId` /
 * `patchedNodeId` (enforced below) — a `delete`/`insert` seam legitimately
 * has only one side, everything else (`match`/`replace`/`ambiguous`) has
 * both. `status: "inconclusive"` carries no such requirement: it may still
 * attach best-effort partial evidence (e.g. an ambiguous candidate seam
 * that wasn't confident enough to report as `"found"`), or none at all
 * when no prohibited-effect producer could be located.
 *
 * `explainedEffectIds` / `patchedEffectIds` are forward-compatible with
 * EL-051's not-yet-built four-gate verdict engine: they're plain effect id
 * references (into `effects.json` on each side) rather than embedded
 * effect payloads, so a future gate evaluator can resolve them against
 * its own copy of the effects artifacts without this schema needing to
 * duplicate `EffectRecord`'s shape or version in lockstep with `effects`.
 */
export const DivergenceStatusSchema = z.enum(["found", "inconclusive"]);
export const DivergenceRecordSchema = z
    .object({
    status: DivergenceStatusSchema,
    /** The earliest baseline node on the causal path to the prohibited effect, when known. */
    baselineNodeId: z.string().min(1).optional(),
    /** Its aligned patched counterpart, when the seam is a paired node rather than a pure insertion/deletion. */
    patchedNodeId: z.string().min(1).optional(),
    /** The `AlignmentRecord.relation` the seam was drawn from, when a seam was identified. */
    alignmentRelation: AlignmentRelationSchema.optional(),
    /** Baseline `effects.json` ids this divergence explains the absence/change of. */
    explainedEffectIds: z.array(z.string().min(1)),
    /** Patched-side `effects.json` ids observed at the seam's patched counterpart, if any. */
    patchedEffectIds: z.array(z.string().min(1)),
    reasons: z.array(z.string().min(1)),
})
    .refine((record) => record.status === "inconclusive" ||
    record.baselineNodeId !== undefined ||
    record.patchedNodeId !== undefined, {
    message: "a 'found' divergence must reference evidence on at least one side",
    path: ["baselineNodeId"],
});
/** The `divergence.json` artifact written once the first causal divergence is selected. */
export const DivergenceFileSchema = z.object({
    schemaVersion: z.literal("1.0"),
    caseId: z.string().min(1),
    divergence: DivergenceRecordSchema,
});
//# sourceMappingURL=schema.js.map